import { z } from 'zod';
/**
 * Contratos de autenticación.
 *
 * El correo se normaliza a minúsculas y sin espacios en el propio esquema: si
 * la normalización viviera en cada endpoint, tarde o temprano uno la olvida y
 * aparecen dos cuentas para el mismo correo.
 */
export const esquemaCorreo = z
    .string({ required_error: 'Falta el correo' })
    .trim()
    .toLowerCase()
    .email('El correo no tiene un formato válido')
    .max(190, 'El correo es demasiado largo');
export const esquemaNombre = z
    .string({ required_error: 'Falta el nombre' })
    .trim()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(160, 'El nombre es demasiado largo');
/* El largo se valida acá y la fortaleza en el servidor, que tiene la lista de
 * contraseñas comunes. Duplicar el mínimo evita un viaje para el caso obvio. */
export const esquemaClave = z
    .string({ required_error: 'Falta la contraseña' })
    .min(12, 'La contraseña debe tener al menos 12 caracteres')
    .max(200, 'La contraseña es demasiado larga');
export const esquemaRegistro = z.object({
    nombre: esquemaNombre,
    email: esquemaCorreo,
    clave: esquemaClave,
});
export const esquemaEntrar = z.object({
    email: esquemaCorreo,
    clave: z.string({ required_error: 'Falta la contraseña' }).min(1, 'Falta la contraseña'),
});
export const esquemaRecuperar = z.object({ email: esquemaCorreo });
export const esquemaRestablecer = z.object({
    token: z.string().min(20, 'El enlace no es válido'),
    clave: esquemaClave,
});
export const esquemaVerificar = z.object({
    token: z.string().min(20, 'El enlace no es válido'),
});
/**
 * Lo ÚNICO que una persona puede cambiar de su propio perfil.
 *
 * `.strict()` es deliberado: una petición que traiga `rol`, `proveedorId`,
 * `clienteId` o `activo` se rechaza con 400 en vez de ignorar el campo en
 * silencio. En la v1 esos campos eran auto-editables y permitían repuntar la
 * propia organización a la de la competencia, o reactivarse tras una
 * suspensión.
 */
export const esquemaEditarPerfilPropio = z.object({ nombre: esquemaNombre }).strict();
export const ROLES = ['admin', 'operador', 'proveedor', 'cliente', 'pendiente'];
/**
 * Nivel dentro de la empresa. El segundo eje, junto a `rol`.
 *
 * `rol` dice de qué lado del mostrador está la cuenta; el nivel dice qué puede
 * hacer dentro de la suya. El orden del arreglo es de MÁS a MENOS poder y la
 * interfaz lo respeta al pintar el selector: leer una lista de permisos en orden
 * arbitrario obliga a compararlos de a pares.
 */
export const NIVELES_EMPRESA = ['administrador', 'operativo', 'lector'];
/** Perfil tal como lo ve su dueño. */
export const esquemaYo = z.object({
    id: z.string(),
    nombre: z.string(),
    email: z.string(),
    rol: z.enum(ROLES),
    activo: z.boolean(),
    correoVerificado: z.boolean(),
    proveedorId: z.string().nullable(),
    clienteId: z.string().nullable(),
    /**
     * Nivel dentro de la empresa. `null` para el equipo y las cuentas pendientes,
     * que no pertenecen a una.
     *
     * Viaja al navegador para que la interfaz pueda esconder lo que no se puede
     * hacer —el botón de comprar a un lector, la pestaña de cuentas a quien no es
     * administrador—. Es una CORTESÍA, no un control: cada endpoint lo comprueba
     * por su cuenta, porque esconder un botón no impide llamar a la API.
     */
    nivelEmpresa: z.enum(NIVELES_EMPRESA).nullable(),
    /** Razón social de su organización, para mostrarla en el encabezado. */
    organizacion: z.string().nullable(),
});
// ─── Cuentas de la propia empresa ───────────────────────────────────────────
/**
 * Una cuenta de la empresa, tal como la ve su administrador.
 *
 * Lleva el correo y el nombre porque son con lo que se identifica a una persona
 * al repartir accesos. NO lleva `clienteId` ni `proveedorId`: son todas de la
 * misma empresa —la de quien pregunta— y repetir el id en cada fila solo daría
 * una vía más por donde filtrarlo.
 */
export const esquemaCuentaEmpresa = z.object({
    id: z.string(),
    nombre: z.string(),
    email: z.string(),
    nivelEmpresa: z.enum(NIVELES_EMPRESA),
    activo: z.boolean(),
    /** Sin verificar, la persona puede entrar pero conviene saberlo al asignar. */
    correoVerificado: z.boolean(),
    creadoEn: z.string(),
    /** La fila de quien está mirando: la interfaz la marca y protege. */
    esUstedMismo: z.boolean(),
});
/**
 * Lo que un administrador de empresa puede cambiar de una cuenta.
 *
 * NO están el rol, la organización, el nombre ni el correo, y ninguna ausencia
 * es un olvido: mover una cuenta de una empresa a otra es de Compra Norte, y
 * dejar que un administrador reescriba el correo de otra persona sería darle una
 * vía para apropiarse de esa cuenta con un restablecimiento de clave.
 */
export const esquemaCambiarCuenta = z
    .object({
    nivelEmpresa: z.enum(NIVELES_EMPRESA).optional(),
    activo: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'No hay nada que cambiar' });
/** Cambio de rol y organización. Solo admin. */
export const esquemaAsignarAcceso = z
    .object({
    rol: z.enum(ROLES).optional(),
    proveedorId: z.string().nullable().optional(),
    clienteId: z.string().nullable().optional(),
    activo: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'No hay nada que cambiar' });
//# sourceMappingURL=auth.js.map